import LoadingButton from '@mui/lab/LoadingButton';
import {
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { ScheduledTask } from '../../../../hooks/useMessageUsersScheduledSessions';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    scheduledTasks: ScheduledTask[];
    refetchScheduledTasks: () => void;
    bulkDeleteScheduledTasks: (scheduledTaskIds: string[]) => Promise<{ success: boolean }>;
    bulkDeleteScheduledTasksLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'nextRepostDate';
type SortDirection = 'asc' | 'desc';

const MessageUsersPriorityMessageBulkDeleteDialog = (props: Props) => {
    const { scheduledTasks, refetchScheduledTasks, bulkDeleteScheduledTasks, bulkDeleteScheduledTasksLoading, open, setOpen } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedScheduledTaskIds, setSelectedScheduledTaskIds] = useState<string[]>([]);
    const [selectedScheduledTasks, setSelectedScheduledTasks] = useState<ScheduledTask[]>([]);

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Sort the auto reposts based on current sort settings
    const sortedScheduledTasks = useMemo(() => {
        return [...scheduledTasks].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.payload?.name?.toLowerCase();
                    bValue = b.payload?.name?.toLowerCase();
                    break;
                case 'nextRepostDate':
                    aValue = new Date(a.scheduleDateTime).getTime();
                    bValue = new Date(b.scheduleDateTime).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [scheduledTasks, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        // Reset selected auto reposts when the dialog is closed
        setSelectedScheduledTasks([]);
        setSelectedScheduledTaskIds([]);
        // Close the dialog
        setOpen(false);
    };

    // Handle delete action
    const handleDelete = () => {
        dialog
            .confirm({
                title: 'Delete Priority Messages',
                message: `Are you sure you want to delete ${selectedScheduledTasks.length.toLocaleString()} Priority Messages?`,
                ok: {
                    text: 'Delete',
                    color: 'error',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                // Perform delete action
                bulkDeleteScheduledTasks(selectedScheduledTaskIds)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar('Priority Messages deleted successfully', {
                                variant: 'success',
                            });

                            refetchScheduledTasks();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to delete Priority Messages', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to delete Priority Messages', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title="Priority Message Bulk Delete" />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        Select the Priority Messages you want to delete. You can select all Priority Messages by checking the checkbox in
                        the header.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedScheduledTasks.length.toLocaleString()} Priority Messages selected
                    </Typography>

                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            backgroundColor: theme => alpha(theme.palette.error.main, 0.1),
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 400,
                                // Reduce the padding between the cells
                                '& .MuiTableCell-root': {
                                    padding: '4px 8px',
                                },
                                // remove the left padding of the first cell
                                '& .MuiTableCell-root:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // remove the right padding of the last cell
                                '& .MuiTableCell-root:last-of-type': {
                                    paddingRight: 2,
                                },
                            }}
                        >
                            <Table
                                size="small"
                                // stickyHeader
                                sx={{
                                    // Pin the header row
                                    '& thead th': {
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'rgb(239, 210, 208)', // (theme: Theme) => alpha(theme.palette.error.main, 0.2),
                                        zIndex: 1,
                                    },
                                    // Pin the first column
                                    '& th:first-of-type, & td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        backgroundColor: 'rgb(239, 210, 208)', // (theme: Theme) => alpha(theme.palette.error.main, 0.2),
                                        // pin the first column
                                        '& th:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            // every cell in the header row
                                            '& th': {
                                                backgroundColor: theme => theme.palette.background.paper,
                                                color: theme => theme.palette.text.primary,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    >
                                        <TableCell width={50} align="center">
                                            <Checkbox
                                                indeterminate={
                                                    selectedScheduledTaskIds.length > 0 &&
                                                    selectedScheduledTaskIds.length < sortedScheduledTasks.length
                                                }
                                                checked={selectedScheduledTasks.length === sortedScheduledTasks.length}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (e.target.checked) {
                                                        setSelectedScheduledTaskIds(
                                                            sortedScheduledTasks.map(autoRepost => autoRepost._id!),
                                                        );
                                                        setSelectedScheduledTasks(sortedScheduledTasks);
                                                    } else {
                                                        setSelectedScheduledTaskIds([]);
                                                        setSelectedScheduledTasks([]);
                                                    }
                                                }}
                                                color="error"
                                                size="small"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>
                                            <TableSortLabel
                                                active={sortField === 'nextRepostDate'}
                                                direction={sortField === 'nextRepostDate' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('nextRepostDate')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Scheduled Date
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedScheduledTasks.map(scheduledTask => (
                                        <TableRow
                                            key={scheduledTask._id}
                                            sx={{
                                                '&:last-child td, &:last-child th': {
                                                    border: 0,
                                                },
                                                backgroundColor: theme =>
                                                    selectedScheduledTaskIds.includes(scheduledTask._id!)
                                                        ? alpha(theme.palette.error.main, 0.2)
                                                        : 'transparent',
                                            }}
                                        >
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={selectedScheduledTaskIds.includes(scheduledTask._id!)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        if (e.target.checked) {
                                                            setSelectedScheduledTaskIds([...selectedScheduledTaskIds, scheduledTask._id!]);
                                                            setSelectedScheduledTasks([...selectedScheduledTasks, scheduledTask]);
                                                        } else {
                                                            setSelectedScheduledTaskIds(
                                                                selectedScheduledTaskIds.filter(id => id !== scheduledTask._id),
                                                            );
                                                            setSelectedScheduledTasks(
                                                                selectedScheduledTasks.filter(ar => ar._id !== scheduledTask._id),
                                                            );
                                                        }
                                                    }}
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.primary" noWrap>
                                                    {scheduledTask.payload?.name || 'No Name'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {moment(scheduledTask.scheduleDateTime).format('DD/MM/YYYY hh:mm A')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="error"
                    size="small"
                    loading={bulkDeleteScheduledTasksLoading}
                    disabled={selectedScheduledTaskIds.length === 0}
                    onClick={() => {
                        handleDelete();
                    }}
                >
                    <span>Delete Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MessageUsersPriorityMessageBulkDeleteDialog;
